import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, Gauge, Lock } from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { dimLabel } from '@/components/client/PerfProfileView';

export default function AdminTalentPerformance() {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState([]);
  const [cid, setCid] = useState('');
  const [cycles, setCycles] = useState(null);
  const [patterns, setPatterns] = useState(null);

  useEffect(() => {
    api.get('/admin/clients').then((r) => {
      const list = (r.data?.items || r.data || []).filter((c) => c.status !== 'archived');
      setCompanies(list);
      if (list.length) setCid(list[0].id);
    }).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const load = useCallback(() => {
    if (!cid) return;
    setCycles(null);
    api.get(`/admin/talentperformance/${cid}/cycles`).then((r) => setCycles(r.data)).catch((e) => { setCycles([]); toast.error(formatApiError(e)); });
    api.get(`/admin/talentperformance/${cid}/patterns`).then((r) => setPatterns(r.data)).catch(() => setPatterns(null));
  }, [cid]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen" data-testid="admin-talentperformance">
      <Topbar title="TalentPerformance" subtitle={t('talentperformance.admin.subtitle', { defaultValue: 'Performance-cycle oversight (aggregated — individual results stay with the client)' })} />
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={cid} onValueChange={setCid}>
            <SelectTrigger className="w-full sm:w-96 max-w-full min-w-0 [&>span]:truncate" data-testid="tp-admin-company"><SelectValue /></SelectTrigger>
            <SelectContent>
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="tp-admin-confidential-note">
          <Lock className="h-3.5 w-3.5 shrink-0" />
          {t('talentperformance.admin.confidentialNote', { defaultValue: 'CTH roles supervise completion and aggregated patterns only. Individual profiles and raw responses are confidential to the client\u2019s HR.' })}
        </p>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
          <Card className="xl:col-span-2 shadow-card overflow-hidden min-w-0">
            <div className="p-4 border-b flex items-center gap-2">
              <Gauge className="h-4 w-4 text-info" />
              <h4 className="text-sm font-semibold">{t('talentperformance.admin.cycles', { defaultValue: 'Evaluation cycles (oversight)' })}</h4>
            </div>
            {cycles === null ? (
              <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>
            ) : cycles.length === 0 ? (
              <p className="p-8 text-sm text-muted-foreground text-center" data-testid="tp-admin-empty">{t('talentperformance.cycle.empty', { defaultValue: 'No evaluation cycles yet.' })}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[560px]">
                  <thead>
                    <tr className="border-b bg-muted/40 text-left text-[12px] uppercase tracking-wide text-muted-foreground">
                      <th className="px-4 py-2.5 font-medium">{t('talentperformance.fields.cycle', { defaultValue: 'Cycle' })}</th>
                      <th className="px-4 py-2.5 font-medium">{t('talentperformance.fields.progress', { defaultValue: 'Progress' })}</th>
                      <th className="px-4 py-2.5 font-medium">{t('talentperformance.fields.status', { defaultValue: 'Status' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cycles.map((c) => (
                      <tr key={c.id} className="border-b last:border-0" data-testid={`tp-admin-cycle-${c.id}`}>
                        <td className="px-4 py-2.5 font-medium">{c.name}</td>
                        <td className="px-4 py-2.5">{c.evaluations_done}/{c.evaluations_total}</td>
                        <td className="px-4 py-2.5"><Badge variant="outline">{t(`talentperformance.status.${c.status}`)}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card className="p-4 shadow-card space-y-2 min-w-0">
            <h4 className="text-sm font-semibold">{t('talentperformance.admin.patterns', { defaultValue: 'Aggregated dimension patterns' })}</h4>
            {patterns?.suppressed && patterns?.evaluations_with_results > 0 ? (
              <p className="text-sm text-muted-foreground flex items-start gap-1.5" data-testid="tp-admin-patterns-suppressed">
                <Lock className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                {t('talentperformance.admin.patternsSuppressed', { defaultValue: 'Aggregated results are hidden to protect confidentiality — fewer than {{n}} evaluations with results.', n: patterns.min_group })}
              </p>
            ) : patterns && Object.keys(patterns.dimensions_avg || {}).length > 0 ? (
              <div className="space-y-1.5" data-testid="tp-admin-patterns">
                {Object.entries(patterns.dimensions_avg).sort((a, b) => b[1] - a[1]).map(([dim, avg]) => (
                  <div key={dim} className="flex items-center justify-between text-sm">
                    <span className="truncate">{dimLabel(t, dim)}</span>
                    <Badge variant="outline">{avg}</Badge>
                  </div>
                ))}
                <p className="text-[11px] text-muted-foreground pt-1">
                  {t('talentperformance.admin.patternsNote', { defaultValue: '{{n}} evaluation(s) with results.', n: patterns.evaluations_with_results })}
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t('talentperformance.admin.noPatterns', { defaultValue: 'Aggregated patterns will appear once evaluations have results.' })}</p>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
